// dependencies
import React, { Suspense, lazy, useMemo, useState } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faCircleCheck as faCircleCheckOutline } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ICellRendererParams, Icon, PYColumnDef, Table, Text, Tooltip } from "@performyard/py-components";

// Components
import HorizontalPercentageBar from "../../../../../../components/HorizontalPercentageBar";
import OptionsDrawer from "../../../../../../components/OptionsDrawer";

import { useAiSettings } from "../../../../../../api/hooks";
import { type QuestionRow, scoresToPercentages } from "./useQuestionTableRows";

import { getColor } from "../../../../../../utils/getColor";

import Config from "../../../../Config";
import { IEngagementFilter } from "../../../../schema";
// styles
import css from "./QuestionsTable.module.scss";

const { DISTRIBUTION_COLUMN_WIDTH } = Config.QUESTIONS.TABLE;
const PERCENTAGE_BAR_WIDTH = DISTRIBUTION_COLUMN_WIDTH - 40;
const MIN_SECTION_PIXELS = 32;
const REPLACEMENT_SECTION_PIXELS = 16;

const EngagementAnalysisModule = lazy(() =>
	import("../../../../../../components/AIModules/EngagementInsight").then((mod) => ({
		default: mod.EngagementAnalysisModule,
	}))
);

// --- Distribution bar helpers ---

const colorMap = {
	1: getColor("primary", 2),
	2: getColor("primary", 3),
	3: getColor("primary", 4),
	4: getColor("primary", 7),
	5: getColor("primary", 9),
};

const scoreMap = {
	1: "Strongly Disagree",
	2: "Disagree",
	3: "Neither Agree nor Disagree",
	4: "Agree",
	5: "Strongly Agree",
};

const blankSections = [
	{
		key: "1",
		percentage: 100,
		color: getColor("neutral", 2),
	},
];

const processSections = (scoresMap: Record<1 | 2 | 3 | 4 | 5, number>) => {
	const minFullTextPercent = (MIN_SECTION_PIXELS / PERCENTAGE_BAR_WIDTH) * 100;
	const replacementPercent = (REPLACEMENT_SECTION_PIXELS / PERCENTAGE_BAR_WIDTH) * 100;

	const inadequateSections = Object.entries({ ...scoresMap })
		.filter(([, percentage]) => percentage < replacementPercent && percentage > 0)
		.map((section) => section[0]);

	const inadequateTotalPercent = inadequateSections.reduce((acc, score) => acc + scoresMap[score], 0);
	const replacementTotalPercent = inadequateSections.length * replacementPercent;
	const remainingPercentage = 100 - replacementTotalPercent + inadequateTotalPercent;

	return [1, 2, 3, 4, 5].map((score) => {
		const percentage = scoresMap[score];
		const inadequate = inadequateSections.includes(score.toString());
		const wholeNumberPercentage = Math.max(Math.round(percentage), 1);
		const shortenText = wholeNumberPercentage <= minFullTextPercent;
		const recalculatedPercentage = inadequate ? replacementPercent : (percentage / 100) * remainingPercentage;
		const text = shortenText ? `${score}` : `${score} (${wholeNumberPercentage}%)`;
		const hoverEffects: "grow"[] = ["grow"];
		return {
			percentage: recalculatedPercentage,
			color: colorMap[score],
			text,
			tooltipText: [`${scoreMap[score]} (${score})`, `${wholeNumberPercentage}% of Responses`],
			hoverEffects,
			key: `${score}`,
		};
	});
};

// --- Cell Renderers ---

const disabledCellStyle = (params: { data?: QuestionRow }) => ({
	color: params.data?.enabled ? "inherit" : "var(--py-neutral-3)",
});

const QuestionTextCellRenderer = (params: ICellRendererParams<QuestionRow>) => {
	const row = params.data;
	if (!row) return null;
	const textColor = row.enabled ? "neutral-9" : "neutral-3";

	return (
		<div className={css.questionWrapper}>
			<Tooltip content={row.question_text} asChild>
				<Text color={textColor} size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1">
					{row.question_text}
				</Text>
			</Tooltip>
			{!row.enabled && (
				<span className={css.infoIcon}>
					<Tooltip content="This question has been disabled and is not being included in any future surveys.">
						<FontAwesomeIcon className="text-[9px]" icon={faCircleInfo} />
					</Tooltip>
				</span>
			)}
		</div>
	);
};

const DistributionCellRenderer = (params: ICellRendererParams<QuestionRow>) => {
	const row = params.data;
	if (!row) return null;

	const distributionData = row.distribution;
	const scoresMap = scoresToPercentages(distributionData);
	const sections =
		distributionData.total_response_count > 0 && Object.keys(distributionData.score_to_count).length > 0
			? processSections(scoresMap)
			: blankSections;

	return <HorizontalPercentageBar sections={sections} fullWidth={PERCENTAGE_BAR_WIDTH} />;
};

const SatisfactionCellRenderer = (params: ICellRendererParams<QuestionRow>) => {
	const row = params.data;
	if (!row) return null;

	return (
		<Tooltip
			content={
				row.included_in_satisfaction
					? "This question is included in engagement satisfaction metrics"
					: "This question is not included in engagement satisfaction metrics"
			}
			dangerouslySetClassName="max-w-[151px]"
		>
			<FontAwesomeIcon
				icon={row.included_in_satisfaction ? faCircleCheck : faCircleCheckOutline}
				color={row.included_in_satisfaction ? "var(--py-primary-5)" : "var(--py-neutral-3)"}
			/>
		</Tooltip>
	);
};

const OptionsCellRenderer = (params: ICellRendererParams<QuestionRow> & { isAIAnalysisEnabled: boolean }) => {
	const row = params.data;
	const context = params.context as QuestionsTableContext;
	if (!row) return null;

	if (row.scale !== "text" && row.question_id !== "eNPSAdditionalFeedback") {
		return null;
	}

	const options: { id: string; label: string | React.ReactNode }[] = [{ id: "download_csv", label: "Download CSV" }];

	if (params.isAIAnalysisEnabled && row.total_response_count > 0) {
		options.push({
			id: "ai_analysis",
			label: (
				<div data-testid="ai-analysis-button" className="flex items-center justify-center gap-1">
					<Icon icon={faSparkles} />
					<Text size="caption" weight="regular" color="neutral-5">
						Summarize
					</Text>
				</div>
			),
		});
	}

	return (
		<OptionsDrawer
			openOn="bottom"
			horizontal
			options={options}
			onSelect={(option) => {
				if (option.id === "download_csv") {
					if (row.question_id === "eNPSAdditionalFeedback") {
						context.handleDownloadNPSAdditionalFeedback();
					} else {
						context.handleDownloadQuestionAnswers(row.question_id);
					}
				} else if (option.id === "ai_analysis") {
					context.onOpenAnalysis(row.question_id);
				}
			}}
		/>
	);
};

// --- Column Definitions ---

const createColumnDefs = (customFactorsEnabled: boolean, isAIAnalysisEnabled: boolean): PYColumnDef<QuestionRow>[] => [
	...(customFactorsEnabled
		? [
				{
					headerName: "Type",
					field: "type" as const,
					sortable: true,
					width: 80,
					resizable: false,
					suppressSizeToFit: true,
					cellStyle: disabledCellStyle,
				},
			]
		: []),
	{
		headerName: "Factors",
		field: "factor",
		sortable: true,
		width: 120,
		resizable: false,
		suppressSizeToFit: true,
		cellStyle: disabledCellStyle,
	},
	{
		headerName: "Questions",
		field: "question_text",
		sortable: true,
		flex: 1,
		resizable: false,
		cellRenderer: QuestionTextCellRenderer,
	},
	{
		headerName: "Avg Score",
		field: "average_score",
		sortable: true,
		width: 80,
		resizable: false,
		suppressSizeToFit: true,
		valueFormatter: (params) => {
			if (params.value == null) return "-";
			return String(params.value);
		},
		cellStyle: (params) => ({
			justifyContent: "center",
			color: params.data?.enabled ? "inherit" : "var(--py-neutral-3)",
		}),
	},
	{
		headerName: "Distribution of Ratings",
		field: "distribution",
		sortable: false,
		width: DISTRIBUTION_COLUMN_WIDTH,
		resizable: false,
		suppressSizeToFit: true,
		cellRenderer: DistributionCellRenderer,
	},
	{
		headerName: "# of Responses",
		field: "total_response_count",
		sortable: true,
		width: 120,
		resizable: false,
		suppressSizeToFit: true,
		cellStyle: (params) => ({
			justifyContent: "center",
			color: params.data?.enabled ? "inherit" : "var(--py-neutral-3)",
		}),
	},
	...(customFactorsEnabled
		? [
				{
					headerName: "",
					field: "included_in_satisfaction" as const,
					sortable: false,
					width: 40,
					resizable: false,
					suppressSizeToFit: true,
					cellRenderer: SatisfactionCellRenderer,
				},
			]
		: []),
	{
		headerName: "",
		field: "question_id" as const,
		sortable: false,
		width: 50,
		resizable: false,
		suppressSizeToFit: true,
		cellRenderer: OptionsCellRenderer,
		cellRendererParams: { isAIAnalysisEnabled },
	},
];

// --- Component ---

type QuestionsTableContext = {
	handleDownloadQuestionAnswers: (questionId: string) => void;
	handleDownloadNPSAdditionalFeedback: () => void;
	onOpenAnalysis: (questionId: string) => void;
};

type QuestionsTableProps = {
	rows: QuestionRow[];
	handleDownloadQuestionAnswers: (questionId: string) => void;
	selectedCohort: string;
	filter: IEngagementFilter;
	handleDownloadNPSAdditionalFeedback: () => void;
	isLoading: boolean;
	customFactorsEnabled?: boolean;
};

const QuestionsTable = ({
	rows,
	handleDownloadQuestionAnswers,
	selectedCohort,
	filter,
	handleDownloadNPSAdditionalFeedback,
	isLoading,
	customFactorsEnabled = false,
}: QuestionsTableProps) => {
	const [isOpen, setIsOpen] = useState(false);
	// cohort and filter are passed live so the analysis panel always reflects the
	// current selection (PY-13171) — only the question is pinned at open time
	const [analysisQuestionId, setAnalysisQuestionId] = useState<string | null>(null);

	const { data: AiSettings } = useAiSettings();
	const isAIAnalysisEnabled = !!AiSettings?.engagementAnalysisEnabled;

	const tableContext: QuestionsTableContext = useMemo(
		() => ({
			handleDownloadQuestionAnswers,
			handleDownloadNPSAdditionalFeedback,
			onOpenAnalysis: (questionId: string) => {
				setIsOpen(true);
				setAnalysisQuestionId(questionId);
			},
		}),
		[handleDownloadQuestionAnswers, handleDownloadNPSAdditionalFeedback]
	);

	const columnDefs = useMemo(
		() => createColumnDefs(customFactorsEnabled, isAIAnalysisEnabled),
		[customFactorsEnabled, isAIAnalysisEnabled]
	);

	return (
		<>
			<Table<QuestionRow>
				autoHeight
				rowData={rows}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data.id}
				emptyStateText="No Questions Found"
				initialState={{
					sort: {
						sortModel: [{ colId: "factor", sort: "asc" }],
					},
				}}
				animateRows={false}
				context={tableContext}
				onGridReady={(params) => params.api.sizeColumnsToFit()}
				onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
			/>
			{isOpen && analysisQuestionId && (
				<Suspense
					fallback={
						<div className="fixed bottom-0 right-0 z-50 m-4 rounded bg-white p-4 shadow">
							<p className="text-sm text-neutral-600">Loading analysis...</p>
						</div>
					}
				>
					<EngagementAnalysisModule
						// remount per selection so retry state resets before the query fires,
						// never leaking a prior Re-summarize into a new cohort/date range
						key={`${analysisQuestionId}:${selectedCohort}:${filter.startDate?.getTime() ?? ""}:${filter.endDate?.getTime() ?? ""}`}
						isOpen={isOpen}
						onOpenChange={setIsOpen}
						selectedCohort={selectedCohort}
						filter={filter}
						questionId={analysisQuestionId}
					/>
				</Suspense>
			)}
		</>
	);
};

export default QuestionsTable;
